/**
 * The draggable split between the template and the 3D view.
 *
 * This lived as an inline `<script>` in the generated shell, which worked right
 * up until the site got a strict Content-Security-Policy. `script-src 'self'`
 * blocks inline execution, so the divider silently stopped working in
 * production — on desktop as well as mobile — while the page carried on looking
 * exactly right. A module file is subject to no such rule, and it means the
 * policy does not have to be weakened for one small behaviour.
 *
 * Pointer events rather than mouse + touch pairs: one code path covers mouse,
 * pen and touch, and pointer capture keeps the drag alive when the cursor
 * outruns the grip.
 */

/** Percentage bounds for the split, so neither pane can be dragged away. */
const MIN_SPLIT = 25;
const MAX_SPLIT = 80;
/** Arrow-key step, in percentage points. */
const STEP = 2;

const clamp = (n: number) => Math.min(MAX_SPLIT, Math.max(MIN_SPLIT, n));

export function mountDivider(root: ParentNode = document): void {
  const stage = root.querySelector<HTMLElement>(".stage");
  const grip = root.querySelector<HTMLElement>(".divider");
  if (!stage || !grip) return;

  const setSplit = (pct: number) => stage.style.setProperty("--split", `${clamp(pct)}%`);
  const applyFromPointer = (clientX: number) => {
    const r = stage.getBoundingClientRect();
    if (!r.width) return;
    setSplit(((clientX - r.left) / r.width) * 100);
  };

  let dragging = false;

  grip.addEventListener("pointerdown", (e) => {
    dragging = true;
    // Capture, so a fast drag that leaves the 6px grip still tracks.
    grip.setPointerCapture(e.pointerId);
    // Stops the browser treating a touch-drag here as a page scroll.
    e.preventDefault();
  });

  grip.addEventListener("pointermove", (e) => {
    if (dragging) applyFromPointer(e.clientX);
  });

  /*
   * pointercancel as well as pointerup: the OS can revoke a pointer mid-drag —
   * a touch becoming a system gesture is the common case — and only pointerup
   * would leave `dragging` true, so the next stray move would jump the split.
   */
  const end = () => {
    dragging = false;
  };
  grip.addEventListener("pointerup", end);
  grip.addEventListener("pointercancel", end);

  // A separator operable only by drag is not operable at all for keyboard users.
  grip.addEventListener("keydown", (e) => {
    const current = Number.parseFloat(getComputedStyle(stage).getPropertyValue("--split")) || 60;
    if (e.key === "ArrowLeft") setSplit(current - STEP);
    else if (e.key === "ArrowRight") setSplit(current + STEP);
    else return;
    e.preventDefault();
  });
}

mountDivider();
